import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../../services/api';
import {
  DisciplineAction,
  DisciplineGridCell,
  DisciplinePreviewGridResponse,
  DisciplineThresholds,
  Intent,
} from '../../../types/discipline';

/**
 * DecisionMapPreview — BAB-64. The V2 replacement for the V1 two-strips.
 *
 * Shows a real engine-driven 2D action map: each cell is the action the
 * Discipline engine would recommend for the worked-example position at
 * that (stock_price, DTE) state, given the current intent + strategy +
 * thresholds. Drag a slider in the parent and the map reflows live.
 *
 * Implementation notes:
 *   - Single source of truth = backend engine (no JS mirror, no drift risk)
 *   - Debounced fetch (300ms) on threshold change — engine call is <50ms
 *     so the perceived latency is the network roundtrip
 *   - 21 × 11 default grid (231 cells) — coarse enough that user can see
 *     boundary shape, fine enough that a 7-pixel cell isn't a hairline
 *   - Hover any cell to see the action + trigger that would fire there
 *   - Color palette reuses `ACTION_COLORS` from the position-card banner
 *     so users see the same color mean the same thing across surfaces
 */

interface Props {
  intent: Intent;
  strategy: 'cc' | 'csp';
  thresholds: DisciplineThresholds;
}

const DEBOUNCE_MS = 300;

/**
 * Trader-theme palette for the settings decision map (Oct 2026). Muted,
 * meaning-preserving fills (cells sit side by side, so saturated colours
 * shout); the shared `ACTION_COLORS` stays untouched for other surfaces.
 * `ACTION_TEXT` is the readable foreground for the same action (the Hold
 * fill is too dark to use as text).
 */
const MAP_FILL: Record<DisciplineAction, string> = {
  'Hold':              '#3a3d43',  // neutral: status quo
  'Close':             '#3f8466',  // muted gain: clean win
  'Roll':              '#56606c',  // neutral-cool grey: continuation
  'Let Assign':        '#8a8f97',  // muted t2: wheel outcome
  'Close at Loss':     '#9a5a52',  // muted loss
  'Close at Debit':    '#9a5a52',  // muted loss
  'Close Both':        '#9a5a52',  // muted loss
  'Stale Data':        '#26282d',  // mute
  'Insufficient Data': '#26282d',  // mute
};
const ACTION_TEXT: Record<DisciplineAction, string> = {
  'Hold':              'var(--tk-t2)',
  'Close':             'var(--tk-gain)',
  'Roll':              'var(--tk-t2)',
  'Let Assign':        'var(--tk-t1)',
  'Close at Loss':     'var(--tk-loss)',
  'Close at Debit':    'var(--tk-loss)',
  'Close Both':        'var(--tk-loss)',
  'Stale Data':        'var(--tk-t3)',
  'Insufficient Data': 'var(--tk-t3)',
};
const GRID_COLS = 11;  // DTE steps
const GRID_ROWS = 21;  // stock-price steps

const DecisionMapPreview: React.FC<Props> = ({ intent, strategy, thresholds }) => {
  const [data, setData] = useState<DisciplinePreviewGridResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [hoverCell, setHoverCell] = useState<{ i: number; j: number } | null>(null);
  const reqIdRef = useRef(0);

  // Stable serialization of just the engine-relevant inputs — avoids re-fetch
  // when irrelevant fields on `thresholds` change (e.g., is_user_override).
  const requestKey = useMemo(() => JSON.stringify({
    intent,
    strategy,
    pt: thresholds.profit_target_pct,
    ts: thresholds.time_stop_dte,
    dd: thresholds.defensive_delta,
    ss: thresholds.stock_stop_pct,
    osm: thresholds.option_stop_multiple,
    mr: thresholds.max_roll_dte_extension,
  }), [intent, strategy, thresholds]);

  useEffect(() => {
    const myId = ++reqIdRef.current;
    setLoading(true);
    const timer = setTimeout(() => {
      apiService.getDisciplinePreviewGrid({
        intent,
        strategy,
        thresholds: {
          profit_target_pct: thresholds.profit_target_pct,
          time_stop_dte: thresholds.time_stop_dte,
          defensive_delta: thresholds.defensive_delta,
          stock_stop_pct: thresholds.stock_stop_pct,
          option_stop_multiple: thresholds.option_stop_multiple,
          max_roll_dte_extension: thresholds.max_roll_dte_extension,
        },
        stock_steps: GRID_ROWS,
        dte_steps: GRID_COLS,
      })
        .then((d) => {
          // Drop stale responses (faster slider drag than network roundtrip)
          if (myId !== reqIdRef.current) return;
          setData(d);
          setErrorMsg(null);
        })
        .catch((e) => {
          if (myId !== reqIdRef.current) return;
          setErrorMsg('Could not compute decision map');
        })
        .finally(() => {
          if (myId === reqIdRef.current) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey]);

  if (errorMsg && !data) {
    return <PreviewShell><div className="st-preview-msg st-is-loss">{errorMsg}</div></PreviewShell>;
  }

  if (!data) {
    return <PreviewShell><div className="st-preview-msg">Computing…</div></PreviewShell>;
  }

  return (
    <PreviewShell>
      <div className="st-preview-k">
        Preview · worked example
      </div>
      <div className="st-preview-ex">
        {data.example.label}
      </div>

      {/* Heatmap. Y axis = stock price % move (rows, top = down move).
          Wait — convention: top of the grid is the LAST stock_axis index
          (largest %), so reading top→down matches "stock moves up→stock
          moves down" intuitively. We flip the row order for display.   */}
      <DecisionGrid
        data={data}
        hoverCell={hoverCell}
        onHover={setHoverCell}
        loading={loading}
      />

      {/* Hover details (or a default explainer when nothing hovered). */}
      <HoverInfo data={data} hoverCell={hoverCell} />

      {/* Legend */}
      <Legend cells={data.grid} />
    </PreviewShell>
  );
};

// ============================================================================
// Subcomponents
// ============================================================================

const PreviewShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="st-preview">
    {children}
  </div>
);

interface GridProps {
  data: DisciplinePreviewGridResponse;
  hoverCell: { i: number; j: number } | null;
  onHover: (c: { i: number; j: number } | null) => void;
  loading: boolean;
}

const DecisionGrid: React.FC<GridProps> = ({ data, hoverCell, onHover, loading }) => {
  // Flip rows so the top of the visible grid = highest stock %  move
  // (stock went up). Indices below are in the FLIPPED display space.
  const displayRows = data.grid.length;
  const displayCols = data.dte_axis.length;
  const flippedGrid = useMemo(() => [...data.grid].reverse(), [data.grid]);
  const flippedStockAxis = useMemo(() => [...data.stock_axis].reverse(), [data.stock_axis]);

  // Compact cell size — settings preview is ~340px wide.
  const cellW = 22;
  const cellH = 14;
  const yLabelW = 38;
  const xLabelH = 16;
  const gridW = cellW * displayCols + yLabelW;
  const gridH = cellH * displayRows + xLabelH;

  return (
    <div className="st-map" style={{ width: gridW, opacity: loading ? 0.6 : 1 }}>
      {/* Stock-axis labels (left edge — flipped: top = up, bottom = down) */}
      {flippedStockAxis.map((pct, i) => {
        // Show every other label to avoid crowding
        if (i % 4 !== 0 && i !== displayRows - 1) return null;
        return (
          <div key={`y-${i}`} className="st-map-y" style={{
            top: i * cellH,
            width: yLabelW - 4,
            height: cellH,
            lineHeight: `${cellH}px`,
          }}>
            {pct >= 0 ? `+${pct.toFixed(0)}` : pct.toFixed(0)}%
          </div>
        );
      })}

      {/* The grid itself */}
      <div style={{ position: 'absolute', left: yLabelW, top: 0 }}>
        {flippedGrid.map((row, i) => (
          <div key={`r-${i}`} className="st-map-row" style={{ height: cellH }}>
            {row.map((cell, j) => (
              <Cell
                key={`c-${i}-${j}`}
                cell={cell}
                width={cellW - 1}
                height={cellH - 1}
                isHovered={hoverCell?.i === i && hoverCell?.j === j}
                onMouseEnter={() => onHover({ i, j })}
                onMouseLeave={() => onHover(null)}
              />
            ))}
          </div>
        ))}
      </div>

      {/* DTE-axis labels (bottom) */}
      {data.dte_axis.map((dte, j) => {
        if (j % 2 !== 0 && j !== displayCols - 1) return null;
        return (
          <div key={`x-${j}`} className="st-map-x" style={{
            left: yLabelW + j * cellW,
            top: cellH * displayRows + 2,
            width: cellW,
          }}>
            {dte}d
          </div>
        );
      })}

      {/* Spacer to push parent height — labels are absolutely positioned */}
      <div style={{ height: gridH }} aria-hidden="true" />
    </div>
  );
};

interface CellProps {
  cell: DisciplineGridCell;
  width: number;
  height: number;
  isHovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const Cell: React.FC<CellProps> = ({ cell, width, height, isHovered, onMouseEnter, onMouseLeave }) => {
  const color = MAP_FILL[cell.action] || '#3a3d43';
  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`st-map-cell ${isHovered ? 'is-hover' : ''}`}
      style={{ width, height, background: color }}
      title={`${cell.action} · ${cell.trigger}`}
      aria-label={`${cell.action}, ${cell.trigger}`}
    />
  );
};

const HoverInfo: React.FC<{
  data: DisciplinePreviewGridResponse;
  hoverCell: { i: number; j: number } | null;
}> = ({ data, hoverCell }) => {
  const display = useMemo(() => {
    if (!hoverCell) {
      return {
        action: null,
        trigger: null,
        stockPct: null,
        dte: null,
        stockPrice: null,
      };
    }
    // hoverCell.i is in flipped-display coordinates; reverse to get real index
    const realRow = data.grid.length - 1 - hoverCell.i;
    const cell = data.grid[realRow][hoverCell.j];
    const stockPct = data.stock_axis[realRow];
    const dte = data.dte_axis[hoverCell.j];
    const stockPrice = data.example.write_or_spot * (1 + stockPct / 100);
    return { action: cell.action, trigger: cell.trigger, stockPct, dte, stockPrice };
  }, [data, hoverCell]);

  if (!display.action) {
    return (
      <div className="st-map-hint">
        Hover any cell to see what the engine would do at that price + DTE.
      </div>
    );
  }

  const action = display.action as DisciplineAction;
  const color = ACTION_TEXT[action] || 'var(--tk-t2)';
  return (
    <div className="st-map-info" style={{ borderLeftColor: MAP_FILL[action] || '#3a3d43' }}>
      <div className="st-map-info-line">
        <span className="st-dim">At </span>
        <b>${display.stockPrice!.toFixed(2)}</b>
        <span className="st-dim"> ({display.stockPct! >= 0 ? '+' : ''}{display.stockPct!.toFixed(0)}%) </span>
        <span className="st-dim">with </span>
        <b>{display.dte} days</b>
        <span className="st-dim"> left:</span>
      </div>
      <div className="st-map-info-act" style={{ color }}>
        {display.action} <span className="st-map-info-trig">
          ({display.trigger})
        </span>
      </div>
    </div>
  );
};

const Legend: React.FC<{ cells: DisciplineGridCell[][] }> = ({ cells }) => {
  const present = useMemo(() => {
    const set = new Set<DisciplineAction>();
    cells.forEach((row) => row.forEach((c) => set.add(c.action)));
    return Array.from(set);
  }, [cells]);

  return (
    <div className="st-map-legend">
      {present.map((action) => (
        <div key={action} className="st-map-legend-item">
          <span className="st-map-swatch" style={{ background: MAP_FILL[action] }} />
          <span>{action}</span>
        </div>
      ))}
    </div>
  );
};

export default DecisionMapPreview;
